import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2, DollarSign, Receipt, Settings, Users, Palette, Mail, FileText,
  CheckCircle2, Circle, ChevronRight, ArrowRight, ArrowLeft, Save, Loader2,
  AlertCircle, Clock, MapPin, Phone, Globe, Shield, Calendar, Sparkles,
  PartyPopper, Rocket, Target, Check
} from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

const SECTION_ICONS = {
  agency_info: Building2,
  payroll: DollarSign,
  billing: Receipt,
  business_rules: Settings,
  users: Users,
  branding: Palette,
  communications: Mail,
  documents: FileText,
};

export default function AgencySetupWizard() {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [sectionData, setSectionData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showGoLive, setShowGoLive] = useState(false);
  const [goLiveResult, setGoLiveResult] = useState(null);
  
  // Helper data
  const [payrollCycles, setPayrollCycles] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [fundingSourceTypes, setFundingSourceTypes] = useState([]);
  const [defaultHolidays, setDefaultHolidays] = useState([]);

  useEffect(() => {
    loadProgress();
    loadHelperData();
  }, []);

  const loadProgress = async () => {
    try {
      setLoading(true);
      const response = await api.get('/setup/progress');
      setProgress(response.data);
      
      // Auto-select first incomplete section
      if (response.data.sections) {
        const firstIncomplete = response.data.sections.find(s => !s.completed);
        if (firstIncomplete && !activeSection) {
          setActiveSection(firstIncomplete.id);
          loadSection(firstIncomplete.id);
        }
      }
    } catch (error) {
      toast.error('Failed to load setup progress');
    } finally {
      setLoading(false);
    }
  };

  const loadHelperData = async () => {
    try {
      const [cycles, zones, types, holidays] = await Promise.all([
        api.get('/setup/payroll-cycles'),
        api.get('/setup/timezones'),
        api.get('/setup/funding-source-types'),
        api.get('/setup/default-holidays'),
      ]);
      setPayrollCycles(cycles.data.cycles || []);
      setTimezones(zones.data.timezones || []);
      setFundingSourceTypes(types.data.types || []);
      setDefaultHolidays(holidays.data.holidays || []);
    } catch (error) {
      console.error('Failed to load helper data', error);
    }
  };

  const loadSection = async (sectionId) => {
    try {
      const response = await api.get(`/setup/sections/${sectionId}`);
      setSectionData(prev => ({
        ...prev,
        [sectionId]: response.data.data || response.data.agency_data || {}
      }));
    } catch (error) {
      toast.error('Failed to load section');
    }
  };

  const handleSectionClick = async (sectionId) => {
    setActiveSection(sectionId);
    if (!sectionData[sectionId]) {
      await loadSection(sectionId);
    }
  };

  const handleFieldChange = (sectionId, field, value) => {
    setSectionData(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        [field]: value
      }
    }));
  };

  const handleSaveSection = async (markComplete = false) => {
    if (!activeSection) return;
    
    setSaving(true);
    try {
      await api.put(`/setup/sections/${activeSection}`, {
        data: sectionData[activeSection] || {},
        mark_complete: markComplete
      });
      
      toast.success(markComplete ? 'Section completed!' : 'Progress saved');
      await loadProgress();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleGoLive = async () => {
    try {
      setLoading(true);
      const response = await api.post('/setup/go-live/verify');
      setGoLiveResult(response.data);
      
      if (response.data.success && response.data.is_operational) {
        setShowGoLive(true);
      } else {
        toast.error('Some requirements are not yet satisfied');
      }
    } catch (error) {
      toast.error('Go-live verification failed');
    } finally {
      setLoading(false);
    }
  };

  const navigateToNextSection = () => {
    if (!progress?.sections || !activeSection) return;
    const currentIndex = progress.sections.findIndex(s => s.id === activeSection);
    if (currentIndex < progress.sections.length - 1) {
      const nextSection = progress.sections[currentIndex + 1];
      handleSectionClick(nextSection.id);
    }
  };

  const navigateToPrevSection = () => {
    if (!progress?.sections || !activeSection) return;
    const currentIndex = progress.sections.findIndex(s => s.id === activeSection);
    if (currentIndex > 0) {
      const prevSection = progress.sections[currentIndex - 1];
      handleSectionClick(prevSection.id);
    }
  };

  // Render section form based on section ID
  const renderSectionForm = () => {
    if (!activeSection) return null;
    const data = sectionData[activeSection] || {};

    switch (activeSection) {
      case 'agency_info':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Agency Name *</Label>
                <Input
                  id="name"
                  value={data.name || ''}
                  onChange={(e) => handleFieldChange(activeSection, 'name', e.target.value)}
                  placeholder="Your Agency Name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={data.phone || ''}
                  onChange={(e) => handleFieldChange(activeSection, 'phone', e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={data.email || ''}
                  onChange={(e) => handleFieldChange(activeSection, 'email', e.target.value)}
                  placeholder="contact@agency.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={data.website || ''}
                  onChange={(e) => handleFieldChange(activeSection, 'website', e.target.value)}
                  placeholder="https://www.agency.com"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Textarea
                id="address"
                value={data.address || ''}
                onChange={(e) => handleFieldChange(activeSection, 'address', e.target.value)}
                placeholder="123 Main Street, Suite 100&#10;City, State ZIP"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="license_number">License Number</Label>
                <Input
                  id="license_number"
                  value={data.license_number || ''}
                  onChange={(e) => handleFieldChange(activeSection, 'license_number', e.target.value)}
                  placeholder="HCA-123456"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ein">EIN (Tax ID)</Label>
                <Input
                  id="ein"
                  value={data.ein || ''}
                  onChange={(e) => handleFieldChange(activeSection, 'ein', e.target.value)}
                  placeholder="12-3456789"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="npi">NPI Number</Label>
                <Input
                  id="npi"
                  value={data.npi || ''}
                  onChange={(e) => handleFieldChange(activeSection, 'npi', e.target.value)}
                  placeholder="1234567890"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="timezone">Timezone</Label>
              <Select
                value={data.timezone || ''}
                onValueChange={(value) => handleFieldChange(activeSection, 'timezone', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  {timezones.map((tz) => (
                    <SelectItem key={tz.id} value={tz.id}>
                      {tz.name} ({tz.offset})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="service_area">Service Area</Label>
              <Textarea
                id="service_area"
                value={data.service_area || ''}
                onChange={(e) => handleFieldChange(activeSection, 'service_area', e.target.value)}
                placeholder="Counties, cities, or ZIP codes you serve"
                rows={2}
              />
            </div>
          </div>
        );

      case 'payroll':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="payroll_cycle">Pay Cycle *</Label>
                <Select
                  value={data.payroll_cycle || ''}
                  onValueChange={(value) => handleFieldChange(activeSection, 'payroll_cycle', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select pay cycle" />
                  </SelectTrigger>
                  <SelectContent>
                    {payrollCycles.map((cycle) => (
                      <SelectItem key={cycle.id} value={cycle.id}>
                        {cycle.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pay_period_start_day">Pay Period Start Day</Label>
                <Select
                  value={data.pay_period_start_day || 'sunday'}
                  onValueChange={(value) => handleFieldChange(activeSection, 'pay_period_start_day', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map((day) => (
                      <SelectItem key={day} value={day}>
                        {day.charAt(0).toUpperCase() + day.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="default_hourly_rate">Default Hourly Rate ($)</Label>
                <Input
                  id="default_hourly_rate"
                  type="number"
                  step="0.01"
                  value={data.default_hourly_rate || ''}
                  onChange={(e) => handleFieldChange(activeSection, 'default_hourly_rate', e.target.value)}
                  placeholder="18.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mileage_rate">Mileage Rate ($/mile)</Label>
                <Input
                  id="mileage_rate"
                  type="number"
                  step="0.001"
                  value={data.mileage_rate || ''}
                  onChange={(e) => handleFieldChange(activeSection, 'mileage_rate', e.target.value)}
                  placeholder="0.67"
                />
              </div>
            </div>

            <div className="p-4 bg-muted/50 rounded-lg">
              <h4 className="font-medium mb-3">Overtime Rules (California)</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <Label className="text-xs">Daily OT After</Label>
                  <Input
                    type="number"
                    value={data.overtime_rules?.daily_overtime_threshold || 8}
                    onChange={(e) => handleFieldChange(activeSection, 'overtime_rules', {
                      ...data.overtime_rules,
                      daily_overtime_threshold: parseInt(e.target.value)
                    })}
                    className="mt-1"
                  />
                  <span className="text-xs text-muted-foreground">hours</span>
                </div>
                <div>
                  <Label className="text-xs">Double Time After</Label>
                  <Input
                    type="number"
                    value={data.overtime_rules?.daily_double_time_threshold || 12}
                    onChange={(e) => handleFieldChange(activeSection, 'overtime_rules', {
                      ...data.overtime_rules,
                      daily_double_time_threshold: parseInt(e.target.value)
                    })}
                    className="mt-1"
                  />
                  <span className="text-xs text-muted-foreground">hours</span>
                </div>
                <div>
                  <Label className="text-xs">Weekly OT After</Label>
                  <Input
                    type="number"
                    value={data.overtime_rules?.weekly_overtime_threshold || 40}
                    onChange={(e) => handleFieldChange(activeSection, 'overtime_rules', {
                      ...data.overtime_rules,
                      weekly_overtime_threshold: parseInt(e.target.value)
                    })}
                    className="mt-1"
                  />
                  <span className="text-xs text-muted-foreground">hours</span>
                </div>
                <div>
                  <Label className="text-xs">OT Multiplier</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={data.overtime_rules?.overtime_multiplier || 1.5}
                    onChange={(e) => handleFieldChange(activeSection, 'overtime_rules', {
                      ...data.overtime_rules,
                      overtime_multiplier: parseFloat(e.target.value)
                    })}
                    className="mt-1"
                  />
                  <span className="text-xs text-muted-foreground">x</span>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium">Holiday Calendar</h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleFieldChange(activeSection, 'holiday_calendar', defaultHolidays)}
                >
                  Load Federal Holidays
                </Button>
              </div>
              <div className="max-h-48 overflow-y-auto border rounded-lg">
                {(data.holiday_calendar || []).map((holiday, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 border-b last:border-0">
                    <span>{holiday.name}</span>
                    <Badge variant="outline">{holiday.date}</Badge>
                  </div>
                ))}
                {(!data.holiday_calendar || data.holiday_calendar.length === 0) && (
                  <div className="p-4 text-center text-muted-foreground">
                    No holidays configured. Click &quot;Load Federal Holidays&quot; to start.
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'billing':
        return (
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium">Funding Sources</h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const sources = data.funding_sources || [];
                    handleFieldChange(activeSection, 'funding_sources', [
                      ...sources,
                      { name: '', type: 'private_pay', contact_name: '', contact_phone: '' }
                    ]);
                  }}
                >
                  Add Source
                </Button>
              </div>
              <div className="space-y-3">
                {(data.funding_sources || []).map((source, idx) => (
                  <div key={idx} className="p-4 border rounded-lg">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Name</Label>
                        <Input
                          value={source.name || ''}
                          onChange={(e) => {
                            const sources = [...(data.funding_sources || [])];
                            sources[idx].name = e.target.value;
                            handleFieldChange(activeSection, 'funding_sources', sources);
                          }}
                          placeholder="Source name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Type</Label>
                        <Select
                          value={source.type || ''}
                          onValueChange={(value) => {
                            const sources = [...(data.funding_sources || [])];
                            sources[idx].type = value;
                            handleFieldChange(activeSection, 'funding_sources', sources);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {fundingSourceTypes.map((t) => (
                              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}
                {(!data.funding_sources || data.funding_sources.length === 0) && (
                  <div className="p-8 text-center text-muted-foreground border rounded-lg">
                    No funding sources added yet
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium">Service Codes</h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const codes = data.service_codes || [];
                    handleFieldChange(activeSection, 'service_codes', [
                      ...codes,
                      { code: '', name: '', default_rate: '', unit: 'hour' }
                    ]);
                  }}
                >
                  Add Code
                </Button>
              </div>
              <div className="space-y-3">
                {(data.service_codes || []).map((code, idx) => (
                  <div key={idx} className="p-4 border rounded-lg">
                    <div className="grid grid-cols-4 gap-3">
                      <div className="space-y-2">
                        <Label>Code</Label>
                        <Input
                          value={code.code || ''}
                          onChange={(e) => {
                            const codes = [...(data.service_codes || [])];
                            codes[idx].code = e.target.value;
                            handleFieldChange(activeSection, 'service_codes', codes);
                          }}
                          placeholder="S5125"
                        />
                      </div>
                      <div className="space-y-2 col-span-2">
                        <Label>Name</Label>
                        <Input
                          value={code.name || ''}
                          onChange={(e) => {
                            const codes = [...(data.service_codes || [])];
                            codes[idx].name = e.target.value;
                            handleFieldChange(activeSection, 'service_codes', codes);
                          }}
                          placeholder="Personal Care"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Rate ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={code.default_rate || ''}
                          onChange={(e) => {
                            const codes = [...(data.service_codes || [])];
                            codes[idx].default_rate = e.target.value;
                            handleFieldChange(activeSection, 'service_codes', codes);
                          }}
                          placeholder="25.00"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                {(!data.service_codes || data.service_codes.length === 0) && (
                  <div className="p-8 text-center text-muted-foreground border rounded-lg">
                    No service codes added yet
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'users':
        return (
          <div className="space-y-6">
            <div className="p-6 bg-muted/50 rounded-lg text-center">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h4 className="font-medium mb-2">User Management</h4>
              <p className="text-muted-foreground mb-4">
                Manage users from the Settings page. You can invite administrators, HR staff, schedulers, and supervisors.
              </p>
              <Button onClick={() => navigate('/settings')}>
                Go to User Management
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">
                  {progress?.go_live_requirements?.find(r => r.id === 'users_created')?.details || 'Users configured'}
                </span>
              </div>
            </div>
          </div>
        );

      case 'branding':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primary_color">Primary Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="primary_color"
                    type="color"
                    value={data.primary_color || '#2563eb'}
                    onChange={(e) => handleFieldChange(activeSection, 'primary_color', e.target.value)}
                    className="w-14 h-10 p-1"
                  />
                  <Input
                    value={data.primary_color || '#2563eb'}
                    onChange={(e) => handleFieldChange(activeSection, 'primary_color', e.target.value)}
                    placeholder="#2563eb"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="secondary_color">Secondary Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="secondary_color"
                    type="color"
                    value={data.secondary_color || '#64748b'}
                    onChange={(e) => handleFieldChange(activeSection, 'secondary_color', e.target.value)}
                    className="w-14 h-10 p-1"
                  />
                  <Input
                    value={data.secondary_color || '#64748b'}
                    onChange={(e) => handleFieldChange(activeSection, 'secondary_color', e.target.value)}
                    placeholder="#64748b"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="logo_url">Logo URL</Label>
              <Input
                id="logo_url"
                value={data.logo_url || ''}
                onChange={(e) => handleFieldChange(activeSection, 'logo_url', e.target.value)}
                placeholder="https://your-agency.com/logo.png"
              />
              <p className="text-xs text-muted-foreground">
                Upload your logo to a hosting service and paste the URL here
              </p>
            </div>
          </div>
        );

      case 'communications':
        return (
          <div className="space-y-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-700 mb-2">Notification Settings</h4>
              <p className="text-sm text-blue-600">
                Configure how Leynix sends notifications to staff and clients.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h5 className="font-medium">Email Notifications</h5>
                  <p className="text-sm text-muted-foreground">Send email notifications for important events</p>
                </div>
                <Switch
                  checked={data.notification_preferences?.email_enabled !== false}
                  onCheckedChange={(checked) => handleFieldChange(activeSection, 'notification_preferences', {
                    ...data.notification_preferences,
                    email_enabled: checked
                  })}
                />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h5 className="font-medium">SMS Notifications</h5>
                  <p className="text-sm text-muted-foreground">Send text messages for urgent alerts</p>
                </div>
                <Switch
                  checked={data.notification_preferences?.sms_enabled || false}
                  onCheckedChange={(checked) => handleFieldChange(activeSection, 'notification_preferences', {
                    ...data.notification_preferences,
                    sms_enabled: checked
                  })}
                />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h5 className="font-medium">Visit Reminders</h5>
                  <p className="text-sm text-muted-foreground">Remind caregivers before scheduled visits</p>
                </div>
                <Switch
                  checked={data.notification_preferences?.visit_reminders !== false}
                  onCheckedChange={(checked) => handleFieldChange(activeSection, 'notification_preferences', {
                    ...data.notification_preferences,
                    visit_reminders: checked
                  })}
                />
              </div>
            </div>
          </div>
        );

      case 'documents':
        return (
          <div className="space-y-6">
            <div className="p-6 bg-muted/50 rounded-lg text-center">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h4 className="font-medium mb-2">Document Templates</h4>
              <p className="text-muted-foreground mb-4">
                Upload your consent forms, HR documents, and care plan templates in the Records Center.
              </p>
              <Button onClick={() => navigate('/records')}>
                Go to Records Center
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">
                  {progress?.go_live_requirements?.find(r => r.id === 'documents_uploaded')?.details || 'Documents configured'}
                </span>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="p-8 text-center text-muted-foreground">
            Select a section to configure
          </div>
        );
    }
  };

  if (loading && !progress) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Go Live Celebration Modal
  if (showGoLive && goLiveResult?.is_operational) {
    return (
      <div className="fixed inset-0 bg-background/95 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="max-w-lg w-full text-center"
        >
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-8 shadow-xl">
            <PartyPopper className="w-16 h-16 mx-auto text-green-600 mb-4" />
            <h1 className="text-3xl font-bold text-green-700 mb-2">Congratulations!</h1>
            <p className="text-xl text-green-600 mb-6">
              {goLiveResult.agency_name} is now operational inside Leynix.
            </p>
            
            <div className="flex items-center justify-center gap-2 text-green-700 mb-6">
              <Sparkles className="w-5 h-5" />
              <span className="font-medium">All {goLiveResult.requirements_satisfied} requirements satisfied</span>
            </div>

            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate('/dashboard')}>
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="outline" onClick={() => setShowGoLive(false)}>
                Continue Setup
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 md:py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Agency Command Center</h1>
        <p className="text-muted-foreground mt-1">
          Configure your agency and prepare for operations
        </p>
      </div>

      {/* Progress Overview */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">Overall Progress</span>
                <span className="text-2xl font-bold">{progress?.overall_progress || 0}%</span>
              </div>
              <Progress value={progress?.overall_progress || 0} className="h-3" />
            </div>
            
            <div className="flex gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{progress?.setup_progress || 0}%</div>
                <div className="text-xs text-muted-foreground">Setup</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{progress?.go_live_progress || 0}%</div>
                <div className="text-xs text-muted-foreground">Go-Live</div>
              </div>
            </div>

            {progress?.is_operational ? (
              <Badge className="bg-green-600">Operational</Badge>
            ) : (
              <Button onClick={handleGoLive} disabled={progress?.go_live_progress < 100}>
                <Rocket className="w-4 h-4 mr-2" />
                Go Live
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Section List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Setup Sections</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <div className="space-y-1">
                {progress?.sections?.map((section) => {
                  const Icon = SECTION_ICONS[section.id] || Settings;
                  const isActive = activeSection === section.id;
                  
                  return (
                    <button
                      key={section.id}
                      onClick={() => handleSectionClick(section.id)}
                      data-testid={`setup-section-${section.id}`}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${
                        isActive
                          ? 'bg-primary/10 text-primary'
                          : 'hover:bg-muted'
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${section.completed ? 'bg-green-100' : 'bg-muted'}`}>
                        {section.completed ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        ) : (
                          <Icon className={`w-4 h-4 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{section.title}</div>
                        <div className="text-xs text-muted-foreground">{section.description}</div>
                      </div>
                      <ChevronRight className={`w-4 h-4 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Go-Live Requirements */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5" />
                Go-Live Checklist
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <div className="space-y-2">
                {progress?.go_live_requirements?.map((req) => (
                  <div
                    key={req.id}
                    data-testid={`go-live-req-${req.id}`}
                    className={`flex items-center gap-3 p-2 rounded-lg ${
                      req.satisfied ? 'bg-green-50' : 'bg-muted/50'
                    }`}
                  >
                    {req.satisfied ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                    ) : (
                      <Circle className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium truncate ${req.satisfied ? 'text-green-700' : ''}`}>
                        {req.title}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{req.details}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Section Form */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>
                {progress?.sections?.find(s => s.id === activeSection)?.title || 'Select a Section'}
              </CardTitle>
              <CardDescription>
                {progress?.sections?.find(s => s.id === activeSection)?.description}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {renderSectionForm()}
            </CardContent>
            {activeSection && (
              <CardFooter className="flex justify-between border-t pt-6">
                <Button variant="outline" onClick={navigateToPrevSection}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => handleSaveSection(false)} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Progress
                  </Button>
                  <Button onClick={() => handleSaveSection(true)} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                    Complete Section
                  </Button>
                </div>
                <Button variant="outline" onClick={navigateToNextSection}>
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardFooter>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
